import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { styled } from '@mui/system';

const MainImage = styled('img')({
    width: '100%',
    maxHeight: 400,
    objectFit: 'cover',
    borderRadius: '8px',
    cursor: 'zoom-in',
});

const ThumbnailImage = styled('img')({
    width: 80,
    height: 60,
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
    marginRight: 8,
    opacity: 1,
    transition: 'opacity 0.3s',
    '&:hover': { opacity: 0.7 },
});

const ThumbnailContainer = styled(Box)({
    display: 'flex',
    overflowX: 'auto',
    marginTop: 8,
});

const ImageGallery = ({ images, currentImageIndex, onImageClick }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);

    if (!images || images.length === 0) {
        return (
            <Box sx={{ height: 200, bgcolor: 'grey.200', borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography>No Image Available</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ position: 'relative' }}>
                <IconButton
                    onClick={() => onImageClick(Math.max(currentImageIndex - 1, 0))}
                    disabled={currentImageIndex === 0}
                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                >
                    <ArrowBackIos />
                </IconButton>

                <MainImage
                    src={images[currentImageIndex].image_url}
                    onClick={() => setLightboxOpen(true)}
                />

                <IconButton
                    onClick={() => onImageClick(Math.min(currentImageIndex + 1, images.length - 1))}
                    disabled={currentImageIndex === images.length - 1}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                >
                    <ArrowForwardIos />
                </IconButton>
            </Box>

            <ThumbnailContainer>
                {images.map((img, index) => (
                    <ThumbnailImage
                        key={img.id || index}
                        src={img.image_url}
                        alt={`Thumbnail ${index}`}
                        onClick={() => onImageClick(index)}
                        style={{ opacity: index === currentImageIndex ? 0.6 : 1 }}
                    />
                ))}
            </ThumbnailContainer>

            {lightboxOpen && (
                <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={currentImageIndex}
                    slides={images.map((img) => ({ src: img.image_url }))}
                    plugins={[Zoom, Thumbnails]}
                    on={{ view: ({ index }) => onImageClick(index) }}
                />
            )}
        </>
    );
};

ImageGallery.propTypes = {
    images: PropTypes.array.isRequired,
    currentImageIndex: PropTypes.number.isRequired,
    onImageClick: PropTypes.func.isRequired,
};

export default ImageGallery;
